// @file Store for user experiments
import {
  LibraryAnalyticsActivateCtaExperimentVariant,
  MakePageSimplifiedLayoutExperimentVariant,
  RecommendedActionsCardExperimentVariant,
  SelfServeFreeTrialExperimentVariant,
} from '@@/enums'
import { defineStore } from 'pinia'
import { ref } from 'vue'

enum UpgradeModalCopyAExperimentVariant {
  Control = 'control',
  Variation = 'variation',
}

enum UpgradeModalSingleStepExperimentVariant {
  Control = 'control',
  Variation = 'variation',
}

const useExperimentsStore = defineStore('experiments', () => {
  // State
  /**
   * Experiment: `upgrade_modal_copy_a`
   */
  const upgradeModalCopyAExperimentVariant = ref<UpgradeModalCopyAExperimentVariant>(
    UpgradeModalCopyAExperimentVariant.Control,
  )

  const upgradeModalSingleStepExperimentVariant = ref<UpgradeModalSingleStepExperimentVariant>(
    UpgradeModalSingleStepExperimentVariant.Control,
  )

  const setUpgradeModalCopyAExperimentVariant = (
    upgradeModalCopyAExperimentVariantValue: UpgradeModalCopyAExperimentVariant | undefined,
  ): void => {
    upgradeModalCopyAExperimentVariant.value =
      upgradeModalCopyAExperimentVariantValue ?? UpgradeModalCopyAExperimentVariant.Control
  }

  const setUpgradeModalSingleStepExperimentVariant = (
    upgradeModalSingleStepExperimentVariantValue: UpgradeModalSingleStepExperimentVariant | undefined,
  ): void => {
    upgradeModalSingleStepExperimentVariant.value =
      upgradeModalSingleStepExperimentVariantValue ?? UpgradeModalSingleStepExperimentVariant.Control
  }

  /**
   * Experiment: `make_page_simplified_layout_experiment`
   */
  const makePageSimplifiedLayoutExperimentVariant = ref<MakePageSimplifiedLayoutExperimentVariant>(
    MakePageSimplifiedLayoutExperimentVariant.Control,
  )

  const setMakePageSimplifiedLayoutExperimentVariant = (
    makePageSimplifiedLayoutExperimentVariantValue: MakePageSimplifiedLayoutExperimentVariant | undefined,
  ): void => {
    makePageSimplifiedLayoutExperimentVariant.value =
      makePageSimplifiedLayoutExperimentVariantValue ?? MakePageSimplifiedLayoutExperimentVariant.Control
  }

  /**
   * Experiment: `self_serve_free_trial_experiment`
   */
  const selfServeFreeTrialExperimentVariant = ref<SelfServeFreeTrialExperimentVariant>(
    SelfServeFreeTrialExperimentVariant.Control,
  )

  const setSelfServeFreeTrialExperimentVariant = (
    selfServeFreeTrialExperimentVariantValue: SelfServeFreeTrialExperimentVariant | undefined,
  ): void => {
    selfServeFreeTrialExperimentVariant.value =
      selfServeFreeTrialExperimentVariantValue ?? SelfServeFreeTrialExperimentVariant.Control
  }

  /**
   * Experiment: `library_analytics_activate_cta_experiment`. null when the user isn't
   * enrolled (or the flip is off), which hides the classroom/team activate card in the
   * library analytics dashboard.
   */
  const libraryAnalyticsActivateCtaExperimentVariant = ref<LibraryAnalyticsActivateCtaExperimentVariant | null>(null)

  const setLibraryAnalyticsActivateCtaExperimentVariant = (
    value: LibraryAnalyticsActivateCtaExperimentVariant | null | undefined,
  ): void => {
    libraryAnalyticsActivateCtaExperimentVariant.value = value ?? null
  }

  /**
   * Experiment: `recommended_actions_card_experiment`. null when the recommended actions card
   * isn't shown to the user, so they aren't in the experiment.
   */
  const recommendedActionsCardExperimentVariant = ref<RecommendedActionsCardExperimentVariant | null>(null)

  const setRecommendedActionsCardExperimentVariant = (
    value: RecommendedActionsCardExperimentVariant | null | undefined,
  ): void => {
    recommendedActionsCardExperimentVariant.value = value ?? null
  }

  return {
    // State
    upgradeModalCopyAExperimentVariant,
    upgradeModalSingleStepExperimentVariant,
    makePageSimplifiedLayoutExperimentVariant,
    selfServeFreeTrialExperimentVariant,
    libraryAnalyticsActivateCtaExperimentVariant,
    recommendedActionsCardExperimentVariant,

    // Action
    setUpgradeModalCopyAExperimentVariant,
    setUpgradeModalSingleStepExperimentVariant,
    setMakePageSimplifiedLayoutExperimentVariant,
    setSelfServeFreeTrialExperimentVariant,
    setLibraryAnalyticsActivateCtaExperimentVariant,
    setRecommendedActionsCardExperimentVariant,
  }
})
export { UpgradeModalCopyAExperimentVariant, UpgradeModalSingleStepExperimentVariant, useExperimentsStore }
